import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { setupAdminUserProfile, checkAdminStatus } from "../utils/adminSetup";
import toast from "react-hot-toast";
import {
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CogIcon,
} from "@heroicons/react/24/outline";

const AdminSetup: React.FC = () => {
  const { user, refreshUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [adminStatus, setAdminStatus] = useState<{
    isAdmin: boolean;
    error?: string;
  }>({ isAdmin: false });

  useEffect(() => {
    checkCurrentAdminStatus();
  }, [user]);

  const checkCurrentAdminStatus = async () => {
    if (!user) {
      return;
    }

    const result = await checkAdminStatus();
    setAdminStatus(result);
  };

  const handleSetupAdmin = async () => {
    if (!user) {
      toast.error("Please log in first");
      return;
    }

    setLoading(true);
    try {
      const result = await setupAdminUserProfile(user.email);

      if (result.success) {
        toast.success(result.message || "Admin setup completed successfully");
        await refreshUserData();
        await checkCurrentAdminStatus();
      } else {
        toast.error(result.error || "Failed to set up admin");
      }
    } catch (error) {
      console.error("Error setting up admin:", error);
      toast.error("Unexpected error during admin setup");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Please Log In
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            You need to be logged in to set up admin access.
          </p>
        </div>
      </div>
    );
  }

  if (adminStatus.isAdmin) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Admin Setup Complete
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {user.email} is configured as an administrator.
          </p>
          <div className="mt-4 p-3 bg-green-50 rounded-md">
            <p className="text-sm text-green-800">
              You now have access to user management and other admin features.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center">
        <CogIcon className="mx-auto h-12 w-12 text-primary-500" />
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          Admin Setup Required
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Set up {user.email} as the system administrator.
        </p>

        <div className="mt-4 p-3 bg-yellow-50 rounded-md">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Important:</p>
              <p>
                This should only be done by the system owner. Admin users can
                create and manage other users, assign pharmacy access, and
                configure system settings.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <div className="text-left">
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <div className="mt-1 p-2 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-900">{user.email}</p>
            </div>
          </div>

          <div className="text-left">
            <label className="block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <div className="mt-1 p-2 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-900">{user.full_name}</p>
            </div>
          </div>

          <div className="text-left">
            <label className="block text-sm font-medium text-gray-700">
              Current Role
            </label>
            <div className="mt-1 p-2 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-900 capitalize">{user.role}</p>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <button
            onClick={handleSetupAdmin}
            disabled={loading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Setting up...
              </>
            ) : (
              <>
                <CogIcon className="h-4 w-4 mr-2" />
                Set Up Admin Access
              </>
            )}
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500">
          <p>
            This will upgrade your account to administrator level with full
            system access.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup;
